import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ScrollText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';

import { useSafeBack } from '@/utils/useSafeBack';
const LAST_UPDATED = 'March 29, 2026';
const TERMS_VERSION = '2.0';

export default function TermsOfUsePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const goBackSafe = useSafeBack('/');
  const [dynamicContent, setDynamicContent] = useState(null);
  const [dynamicDate, setDynamicDate] = useState(null);
  const [loading, setLoading] = useState(true);

  // Per-page title + meta (audit D5.1) — restores on unmount.
  useEffect(() => {
    const prevTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute('content') : null;
    document.title = 'Terms of Use · Eili';
    if (meta) meta.setAttribute('content', 'The rules for using Eili — subscription terms, acceptable use, refunds, and the boundaries between our study companion and your own study.');
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc) meta.setAttribute('content', prevDesc);
    };
  }, []);

  useEffect(() => {
    async function fetchTerms() {
      try {
        const lang = (i18n.language || 'en').split('-')[0];
        const res = await axios.get(`${API}/legal/terms-of-use`, { params: { locale: lang } });
        const servedLocale = res.data.content_locale || 'en';
        if (res.data.content && servedLocale === lang) {
          setDynamicContent(res.data.content);
          if (res.data.updated_at) setDynamicDate(new Date(res.data.updated_at).toLocaleDateString(lang === 'en' ? 'en-US' : lang, { year: 'numeric', month: 'long', day: 'numeric' }));
        }
      } catch { /* fall back to hardcoded */ }
      finally { setLoading(false); }
    }
    fetchTerms();
  }, [i18n.language]);

  const sectionTitle = { color: 'var(--theme-text, #f1f5f9)', fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem' };
  const bodyText = { color: 'var(--theme-textSecondary, #94a3b8)', fontSize: '0.875rem' };
  const listStyle = { color: 'var(--theme-textSecondary, #94a3b8)', fontSize: '0.875rem', paddingLeft: '1.25rem' };

  return (
    <div className="page-container" style={{ background: 'var(--theme-background, #0f172a)', color: 'var(--theme-text, #f1f5f9)' }}>
      <header className="sticky top-0 z-50 px-4 py-4 shadow-sm backdrop-blur-xl" style={{ background: 'var(--theme-surface, #1e293b)', borderBottom: '1px solid var(--glass-border, #334155)' }}>
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={goBackSafe} className="rounded-full h-9 w-9 p-0" data-testid="terms-back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <ScrollText className="h-5 w-5" style={{ color: 'var(--theme-accent, #6366f1)' }} />
          <h1 className="text-base font-semibold">{t('terms.title')}</h1>
        </div>
      </header>
      <main className="page-content max-w-3xl mx-auto px-4 py-8 pb-20">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" style={{ color: 'var(--theme-accent)' }} /></div>
        ) : dynamicContent ? (
          <div className="space-y-6" style={{ lineHeight: 1.75 }}>
            <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--theme-textSecondary, #94a3b8)' }}>
              {t('terms.last_updated')}: {dynamicDate || LAST_UPDATED}
            </p>
            <div
              style={{
                width: '100%',
                maxWidth: 'calc(100vw - 2rem)',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                overscrollBehaviorX: 'contain',
                touchAction: 'pan-x pan-y',
              }}
              data-testid="terms-scroll-wrapper"
            >
              <div className="prose prose-sm prose-invert dynamic-html-content" style={{ color: 'var(--theme-text, #f1f5f9)' }} dangerouslySetInnerHTML={{ __html: dynamicContent }} />
            </div>
          </div>
        ) : (
        <div className="space-y-8" style={{ lineHeight: 1.75 }}>
          <div>
            <p className="text-xs uppercase tracking-wider mb-2" style={{ color: 'var(--theme-textSecondary, #94a3b8)' }}>
              {t('terms.last_updated')}: {LAST_UPDATED} &middot; {t('terms.version')} {TERMS_VERSION}
            </p>
            <p style={bodyText}>{t('terms.intro')}</p>
          </div>

          {/* 1. Acceptance */}
          <Section title={t('terms.s1_title')} style={sectionTitle}>
            <p style={bodyText}>{t('terms.s1_body')}</p>
          </Section>

          {/* 2. Description of Service */}
          <Section title={t('terms.s2_title')} style={sectionTitle}>
            <p style={bodyText}>{t('terms.s2_body')}</p>
          </Section>

          {/* 3. AI Disclaimer */}
          <Section title={t('terms.s3_title')} style={sectionTitle}>
            <p style={bodyText}>{t('terms.s3_important')}</p>
            <ul className="list-disc space-y-2 mt-3" style={listStyle}>
              {t('terms.s3_items', { returnObjects: true }).map((item, i) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
              ))}
            </ul>
          </Section>

          {/* 4. User Accounts */}
          <Section title={t('terms.s4_title')} style={sectionTitle}>
            <ul className="list-disc space-y-2" style={listStyle}>
              {t('terms.s4_items', { returnObjects: true }).map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          </Section>

          {/* 5. Subscriptions */}
          <Section title={t('terms.s5_title')} style={sectionTitle}>
            <ul className="list-disc space-y-2" style={listStyle}>
              {t('terms.s5_items', { returnObjects: true }).map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          </Section>

          {/* 6. Refund Policy */}
          <Section title={t('terms.s6_title')} style={sectionTitle}>
            <p style={bodyText}>{t('terms.s6_body')}</p>
          </Section>

          {/* 7. Acceptable Use */}
          <Section title={t('terms.s7_title')} style={sectionTitle}>
            <p className="mb-3" style={bodyText}>{t('terms.s7_intro')}</p>
            <ul className="list-disc space-y-2" style={listStyle}>
              {t('terms.s7_items', { returnObjects: true }).map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          </Section>

          {/* 8. Community Features */}
          <Section title={t('terms.s8_title')} style={sectionTitle}>
            <p className="mb-3" style={bodyText}>{t('terms.s8_body')}</p>
            <ul className="list-disc space-y-2" style={listStyle}>
              {t('terms.s8_items', { returnObjects: true }).map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          </Section>

          {/* 9. Community Guidelines */}
          <Section title={t('terms.s9_title')} style={sectionTitle}>
            <p className="mb-3" style={bodyText}>{t('terms.s9_body')}</p>
            <ul className="list-disc space-y-2" style={listStyle}>
              {t('terms.s9_items', { returnObjects: true }).map((item, i) => <li key={i}>{item}</li>)}
            </ul>
            <p className="mt-3" style={bodyText}>
              {t('terms.s9_link')}:{' '}
              <Link to="/community-guidelines" className="underline font-medium" style={{ color: 'var(--theme-accent, #6366f1)' }}>
                {t('guidelines.title')}
              </Link>
            </p>
          </Section>

          {/* 10. Share Plan */}
          <Section title={t('terms.s10_title')} style={sectionTitle}>
            <p className="mb-3" style={bodyText}>{t('terms.s10_body')}</p>
            <ul className="list-disc space-y-2" style={listStyle}>
              {t('terms.s10_items', { returnObjects: true }).map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          </Section>

          {/* 11. Data & Privacy */}
          <Section title={t('terms.s11_title')} style={sectionTitle}>
            <p style={bodyText}>
              {t('terms.s11_body')}{' '}
              <button onClick={() => navigate('/privacy')} className="underline font-medium" style={{ color: 'var(--theme-accent, #6366f1)' }}>
                {t('terms.s11_link')}
              </button>
            </p>
          </Section>

          {/* 12. Data Retention */}
          <Section title={t('terms.s12_title')} style={sectionTitle}>
            <ul className="list-disc space-y-2" style={listStyle}>
              {t('terms.s12_items', { returnObjects: true }).map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          </Section>

          {/* 13–20 */}
          {[13, 14, 15, 16, 17, 18, 19, 20].map((n) => (
            <Section key={n} title={t(`terms.s${n}_title`)} style={sectionTitle}>
              <p style={bodyText}>
                {t(`terms.s${n}_body`)}
                {n === 20 && (
                  <>
                    {' '}
                    <a href="mailto:support@askeili.com" className="underline font-medium" style={{ color: 'var(--theme-accent, #6366f1)' }}>
                      {t('generated.termsofusepage_support_askeili_com_7650')}
                    </a>.
                  </>
                )}
              </p>
            </Section>
          ))}
        </div>
        )}
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-base font-semibold mb-3" style={{ color: 'var(--theme-text, #f1f5f9)' }}>{title}</h2>
      <div className="text-sm space-y-2" style={{ color: 'var(--theme-textSecondary, #cbd5e1)' }}>{children}</div>
    </section>
  );
}
